import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { VStack, Text } from "@whoosh/design-system/src/components/primitive";
import { mergeEligibleWindowCharges } from "@whoosh/features/src/Events/utils/mergeEligibleWindowCharges";

import { EventDetailPricingCard } from "./EventDetailPricingCard";
import { EventDetailPricingSection_event$key } from "./__generated__/EventDetailPricingSection_event.graphql";

export const EventDetailPricingSection = ({
  eventRef,
}: {
  eventRef: EventDetailPricingSection_event$key;
}) => {
  const event = useFragment(EventDetailPricingSection_EventFragment, eventRef);

  // Charges are sourced from the windows the client is eligible for, merged and
  // de-duped across windows. The backend already excludes HIDDEN charges from
  // patron responses, so only VISIBLE / SELECTABLE charges reach here.
  const charges = mergeEligibleWindowCharges(event.clientEligibleSignUpWindows);

  const shouldShowPricingSection = charges.length > 0;
  return shouldShowPricingSection ? (
    <VStack gap={4} pb={5}>
      <Text
        maxFontSizeMultiplier={2}
        variant="mainSmallSemiBold"
        py={2}
      >{t`Pricing`}</Text>
      <VStack gap={2}>
        {charges.map(({ node, memberControl }) => (
          <EventDetailPricingCard
            key={node.id}
            displayName={node.chargeRuleSet?.displayName ?? ""}
            displayDescription={node.chargeRuleSet?.displayDescription ?? ""}
            price={
              node.chargeRuleSet?.chargeRules?.edges?.[0]?.node?.price ?? null
            }
            memberControl={memberControl}
            primaryChargeType={event.primaryChargeType ?? "PER_PERSON"}
          />
        ))}
      </VStack>
    </VStack>
  ) : null;
};

export const EventDetailPricingSection_EventFragment = graphql`
  fragment EventDetailPricingSection_event on Event {
    primaryChargeType

    clientEligibleSignUpWindows(clientId: $memberId) {
      charges {
        edges {
          memberControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              displayDescription
              chargeRules {
                edges {
                  node {
                    id
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
